import React, {useMemo} from "react";
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {buildDiscountOptions, isDiscountApplicable, IAncillaryDiscountRange} from "./discount";

export interface IAncillaryDiscountSelectProps {
	/** percentuale sconto selezionata (null = nessuno sconto) */
	value: number | null;
	/** callback di modifica: `null` = nessuno sconto */
	onChange: (value: number | null) => void;
	/**
	 * flag PRC: se `false` il campo NON viene renderizzato (accessorio non scontabile).
	 * Assente/true → scontabile.
	 */
	flagDiscountApplicable?: boolean | null;
	/** range sconto configurato (owner PRC). Se assente si usa il fallback documentato. */
	range?: Partial<IAncillaryDiscountRange> | null;
	/**
	 * gate "solo banco" (VB-04). Il flusso create-ra network È già desk-only, ma il
	 * prop rende esplicito il vincolo e permette di disabilitarlo in contesti non-desk.
	 * Default: true (desk).
	 */
	deskSale?: boolean;
	disabled?: boolean;
	/** identificativo per test/aria (es. ancillaryCode) */
	testId?: string;
}

/**
 * Menù a tendina per lo sconto % per-item degli accessori in emissione RA.
 * - non renderizza nulla se l'accessorio non è scontabile (`flag_discount_applicable=false`)
 *   o se non è una vendita al banco (VB-04);
 * - opzioni entro il range configurato (fallback documentato se assente);
 * - opzione "nessuno sconto" (value `null`) sempre presente.
 */
const AncillaryDiscountSelect = ({
	value,
	onChange,
	flagDiscountApplicable,
	range,
	deskSale = true,
	disabled,
	testId,
}: IAncillaryDiscountSelectProps) => {
	const {t} = useTranslation(["network"]);

	const options = useMemo(() => buildDiscountOptions(range), [range]);

	// VB-04: solo banco + accessorio scontabile
	if (!deskSale || !isDiscountApplicable({flagDiscountApplicable})) {
		return null;
	}

	const NONE = "__none__";

	const handleChange = (event: SelectChangeEvent) => {
		const raw = event.target.value;
		onChange(raw === NONE ? null : Number(raw));
	};

	const label = t("raFilling.ancillariesInfo.discount.label");

	return (
		<FormControl size={"small"} sx={{minWidth: 140}} disabled={disabled} data-testid={testId ? `ancillary-discount-${testId}` : "ancillary-discount"}>
			<InputLabel id={`ancillary-discount-label-${testId ?? "x"}`}>{label}</InputLabel>
			<Select
				labelId={`ancillary-discount-label-${testId ?? "x"}`}
				label={label}
				value={value === null || value === undefined ? NONE : String(value)}
				onChange={handleChange}
				inputProps={{"data-testid": testId ? `ancillary-discount-input-${testId}` : "ancillary-discount-input"}}
			>
				<MenuItem value={NONE}>{t("raFilling.ancillariesInfo.discount.none")}</MenuItem>
				{options.map((opt) => (
					<MenuItem key={opt} value={String(opt)}>
						{opt}%
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

export default AncillaryDiscountSelect;
